import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { formatApiErrorDetail } from "../lib/api";
import useBrandIdentity from "../hooks/useBrandIdentity";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { logo } = useBrandIdentity();
  const navigate = useNavigate();
  const loc = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("CUSTOMER");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [cnpj, setCnpj] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (role === "MERCHANT" && cnpj.replace(/\D/g, "").length !== 14) {
        setError("CNPJ inválido (14 dígitos)"); setLoading(false); return;
      }
      const u = await register({ name, email, password, role, phone, cnpj: role === "MERCHANT" ? cnpj : undefined });
      toast.success(`Conta criada, ${u.name}! Verifique seu email.`);
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <Card className="w-full max-w-md p-8 rounded-2xl shadow-xl border-stone-200">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Morar Bem" className="h-16 w-16 object-contain mb-2"/>
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900 text-center">Criar conta</h1>
        <p className="text-sm text-stone-500 mt-1 text-center">Junte-se ao Morar Bem</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label>Eu sou...</Label>
            <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3 mt-2">
              <label className={`flex flex-col items-start gap-1 p-3 rounded-xl border cursor-pointer transition-colors ${role === "CUSTOMER" ? "border-blue-800 bg-blue-50" : "border-stone-200 bg-white"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="CUSTOMER" id="r-customer" data-testid="role-customer"/>
                  <span className="font-semibold text-sm text-stone-900">Cliente</span>
                </div>
                <span className="text-[11px] text-stone-500">Quero ganhar cashback</span>
              </label>
              <label className={`flex flex-col items-start gap-1 p-3 rounded-xl border cursor-pointer transition-colors ${role === "MERCHANT" ? "border-blue-800 bg-blue-50" : "border-stone-200 bg-white"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="MERCHANT" id="r-merchant" data-testid="role-merchant"/>
                  <span className="font-semibold text-sm text-stone-900">Comerciante</span>
                </div>
                <span className="text-[11px] text-stone-500">Tenho um negócio</span>
              </label>
            </RadioGroup>
          </div>
          <div>
            <Label htmlFor="name">Nome</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" data-testid="register-name"/>
          </div>
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" data-testid="register-email"/>
          </div>
          <div>
            <Label htmlFor="phone">Telefone (também aceito como login)</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(99) 99999-9999" className="rounded-xl mt-1" data-testid="register-phone"/>
          </div>
          {role === "MERCHANT" && (
            <div>
              <Label htmlFor="cnpj">CNPJ da empresa</Label>
              <Input id="cnpj" required value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0000-00" className="rounded-xl mt-1" data-testid="register-cnpj"/>
              <p className="text-[11px] text-stone-500 mt-1">Você completa os dados da empresa (nome, logo, endereço) logo após o cadastro.</p>
            </div>
          )}
          <div>
            <Label htmlFor="password">Senha (mín. 6)</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-1" data-testid="register-password"/>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl h-11" data-testid="register-submit">
            {loading ? "Criando..." : "Criar conta"}
          </Button>
        </form>
        <p className="text-sm text-stone-500 mt-4 text-center">
          Já tem conta? <Link to="/login" className="text-blue-900 font-semibold hover:underline" data-testid="goto-login">Entrar</Link>
        </p>
      </Card>
    </div>
  );
}
